import { createFrontOfficeInstance } from '@/shared/api/api-instance';

export type AiCampaignToneDto = 'friendly' | 'expert' | 'concise';
export type AiCampaignStatusDto =
  | 'draft'
  | 'scheduled'
  | 'running'
  | 'paused'
  | 'completed'
  | 'cancelled'
  | 'failed';

export type AiCampaignDeliveryStatusDto =
  | 'candidate'
  | 'queued'
  | 'reserved'
  | 'published'
  | 'delivered'
  | 'read'
  | 'blocked'
  | 'skipped'
  | 'failed'
  | 'cancelled';

export type CreateAiCampaignDraftsRequestDto = {
  company_id: string;
  channel_ids: string[];
  goal: string;
  audience: string;
  tone: AiCampaignToneDto;
  variants: number;
  instructions?: string;
};

export type AiCampaignRecipientPreviewRequestDto = {
  company_id: string;
  channel_ids: string[];
  query?: string;
  in_contact_list?: boolean;
  limit?: number;
};

export type AiCampaignRecipientPreviewItemDto = {
  channel_id: string;
  chat_id?: string;
  contact_id: string;
  name: string;
  last_name?: string;
  username?: string;
  phone_number?: string;
  protocol?: string;
  in_contact_list: boolean;
};

export type AiCampaignRecipientPreviewResponseDto = {
  items: AiCampaignRecipientPreviewItemDto[];
  limit: number;
  truncated: boolean;
  channel_counts: Array<{
    channel_id: string;
    count: number;
  }>;
};

export type AiCampaignDraftVariantDto = {
  id: string;
  text: string;
  rationale?: string;
};

export type AiCampaignDraftsResponseDto = {
  variants: AiCampaignDraftVariantDto[];
  provider: string;
  model: string;
  knowledge_base_sources: Array<{
    id: string;
    title: string;
    tags: string[];
  }>;
};

export type AiCampaignSegmentDto = {
  channel_ids: string[];
  query?: string;
  in_contact_list?: boolean;
  protocols?: string[];
  exclude_blocked: boolean;
  require_chat: boolean;
  limit: number;
};

export type AiCampaignScheduleDto = {
  mode: 'manual' | 'scheduled';
  start_at: string | null;
  timezone: string;
};

export type AiCampaignContentDto = {
  goal: string;
  audience: string;
  tone: AiCampaignToneDto;
  instructions?: string;
  variants: AiCampaignDraftVariantDto[];
  selected_variant_id: string;
};

export type AiCampaignDto = {
  id: string;
  account_id: string;
  company_id: string;
  name: string;
  status: AiCampaignStatusDto;
  segment: AiCampaignSegmentDto;
  schedule: AiCampaignScheduleDto;
  content: AiCampaignContentDto | null;
  recipient_count: number;
  created_by: string;
  approved_by: string | null;
  created_at: string;
  updated_at: string;
};

export type AiCampaignDeliveryDto = {
  id: string;
  campaign_id: string;
  company_id: string;
  channel_id: string;
  chat_id: string;
  contact_id: string;
  recipient: AiCampaignRecipientPreviewItemDto & {
    chat_id: string;
    blocked: boolean;
  };
  message_preview: string;
  message_hash: string;
  idempotency_key: string;
  outbound_message_id: string | null;
  status: AiCampaignDeliveryStatusDto;
  not_before: string;
  attempt_count: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
};

export type AiCampaignCreateRequestDto = {
  company_id: string;
  name: string;
  segment: AiCampaignSegmentDto;
  schedule: AiCampaignScheduleDto;
};

export type AiCampaignUpdateRequestDto = Partial<{
  name: string;
  segment: AiCampaignSegmentDto;
  schedule: AiCampaignScheduleDto;
  content: AiCampaignContentDto;
}>;

export type AiCampaignsResponseDto = {
  items: AiCampaignDto[];
  limit: number;
};

export type AiCampaignDeliveriesResponseDto = {
  items: AiCampaignDeliveryDto[];
  limit: number;
};

function campaignQuery(companyId: string) {
  const params = new URLSearchParams({ company_id: companyId, limit: '100' });

  return params.toString();
}

export const createAiCampaignDrafts = (data: CreateAiCampaignDraftsRequestDto) =>
  createFrontOfficeInstance<AiCampaignDraftsResponseDto>({
    url: '/api/v1/ai/campaigns/drafts',
    method: 'POST',
    data,
  });

export const previewAiCampaignRecipients = (data: AiCampaignRecipientPreviewRequestDto) =>
  createFrontOfficeInstance<AiCampaignRecipientPreviewResponseDto>({
    url: '/api/v1/ai/campaigns/recipients/preview',
    method: 'POST',
    data,
  });

export const listAiCampaigns = (companyId: string) =>
  createFrontOfficeInstance<AiCampaignsResponseDto>({
    url: `/api/v1/ai/campaigns?${campaignQuery(companyId)}`,
    method: 'GET',
  });

export const createAiCampaign = (data: AiCampaignCreateRequestDto) =>
  createFrontOfficeInstance<AiCampaignDto>({
    url: '/api/v1/ai/campaigns',
    method: 'POST',
    data,
  });

export const updateAiCampaign = (
  id: string,
  data: AiCampaignUpdateRequestDto
) =>
  createFrontOfficeInstance<AiCampaignDto>({
    url: `/api/v1/ai/campaigns/${id}`,
    method: 'PATCH',
    data,
  });

export const prepareAiCampaign = (id: string) =>
  createFrontOfficeInstance<AiCampaignDto>({
    url: `/api/v1/ai/campaigns/${id}/prepare`,
    method: 'POST',
  });

export const startAiCampaign = (id: string) =>
  createFrontOfficeInstance<AiCampaignDto>({
    url: `/api/v1/ai/campaigns/${id}/start`,
    method: 'POST',
  });

export const pauseAiCampaign = (id: string) =>
  createFrontOfficeInstance<AiCampaignDto>({
    url: `/api/v1/ai/campaigns/${id}/pause`,
    method: 'POST',
  });

export const cancelAiCampaign = (id: string) =>
  createFrontOfficeInstance<AiCampaignDto>({
    url: `/api/v1/ai/campaigns/${id}/cancel`,
    method: 'POST',
  });

export const getAiCampaignDeliveries = (id: string) =>
  createFrontOfficeInstance<AiCampaignDeliveriesResponseDto>({
    url: `/api/v1/ai/campaigns/${id}/deliveries?limit=200`,
    method: 'GET',
  });
